/**
 * DotPlotView - 2D scatter plot visualization for comparing metrics
 *
 * Displays players as dots on a 2D chart with shell-hosted axis selectors.
 * Uses `getTooltipComponent` helper to deduplicate themed tooltip configuration
 * across the three scatter plot layers (other players, comparison, current).
 * SE-5568: the X/Y axis selectors render through `FixedPositionDropdownShell`
 * (open-state + positioning owned by the Container via
 * `usePerformanceDropdowns`), matching the SE-5551 Compare/Timeframe pattern.
 * SE-6293: the axis domain and tick values are derived from the plotted data via
 * `resolveDotPlotAxisScales` instead of being pinned to a fixed 0–100 grid, so a
 * rating above 100 no longer falls outside the drawable area.
 * @remarks This is a View component - it holds no state and no business logic;
 * the only hook is a `useMemo` deriving the axis scales from the `players` prop.
 */

import React, { useMemo } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";

import { resolveDotPlotAxisScales } from "@/features/player-detail/utils/dotPlotAxisScale";
import {
  renderMetricShellDropdown,
  renderMetricShellOption,
} from "@/features/player-detail/utils/metricShellDropdown";
import type { PerformanceShellTriggerProps } from "@/features/player-detail/hooks/usePerformanceMenuTriggers";
import type { ShellDropdownWiring } from "@/features/player-detail/utils/performanceAnalyticsViewHelpers";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

const {
  VictoryAxis,
  VictoryChart,
  VictoryScatter,
  VictoryTooltip,
} = require("victory");

/**
 * Player data point for scatter plot
 */
interface PlayerDataPoint {
  readonly id: string;
  readonly name: string;
  readonly x: number | null;
  readonly y: number | null;
  readonly isCurrentPlayer: boolean;
  /** Whether this is a comparison player (shows orange dot) */
  readonly isComparisonPlayer?: boolean;
}

/**
 * Metric option for axis selection
 */
interface MetricOption {
  readonly label: string;
  readonly value: number | null;
}

/**
 * Props for DotPlotView component
 */
interface DotPlotViewProps {
  /** Player data points for scatter plot */
  readonly players: readonly PlayerDataPoint[];
  /** X-axis metric */
  readonly xMetric: MetricOption;
  /** Y-axis metric */
  readonly yMetric: MetricOption;
  /** Available metrics for axis selection */
  readonly availableMetrics: readonly MetricOption[];
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Chart size */
  readonly size: number;
  /** Render function for Y-axis menu trigger (shell-hosted, SE-5568) */
  readonly renderYMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.ReactElement;
  /** Render function for X-axis menu trigger (shell-hosted, SE-5568) */
  readonly renderXMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.ReactElement;
  /** Factory for creating Y-axis metric select handlers */
  readonly createYSelectHandler: (metric: MetricOption) => () => void;
  /** Factory for creating X-axis metric select handlers */
  readonly createXSelectHandler: (metric: MetricOption) => () => void;
  /** Shell wiring for the X-axis dropdown (SE-5568). */
  readonly xAxisDropdown: ShellDropdownWiring;
  /** Shell wiring for the Y-axis dropdown (SE-5568). */
  readonly yAxisDropdown: ShellDropdownWiring;
  /** Tooltip label formatter function */
  readonly tooltipLabelFormatter: (args: {
    datum: { name: string; x: number | null; y: number | null };
  }) => string;
}

/** Current player dot color */
const PLAYER_COLOR = "#22d3ee";

/** Comparison player dot color (orange) */
const COMPARISON_PLAYER_COLOR = "#f97316";

/** Other players dot color */
const OTHER_PLAYER_COLOR = "#6b7280";

/** Satoshi font family */
const FONT_FAMILY = "Satoshi, sans-serif";

/** Highlight color pair for dark/light themes */
interface HighlightColor {
  readonly dark: string;
  readonly light: string;
}

/**
 * Build a themed VictoryTooltip element for scatter plot data points
 * @param isDark - Whether dark mode is active
 * @param highlightColor - Color pair for the highlighted metric values
 * @returns Configured VictoryTooltip JSX element
 */
const getTooltipComponent = (
  isDark: boolean,
  highlightColor: HighlightColor
) => (
  <VictoryTooltip
    flyoutStyle={{
      fill: isDark ? "#18181b" : "#ffffff",
      stroke: isDark ? "#27272a" : "#e4e4e7",
      strokeWidth: 0.5,
    }}
    // eslint-disable-next-line no-restricted-syntax -- Victory chart-lib style API (SVG per-datum label styling), not an RN/atom style
    style={[
      {
        fill: isDark ? "#a1a1aa" : "#52525b",
        fontSize: 5,
        fontFamily: FONT_FAMILY,
      },
      {
        fill: isDark ? highlightColor.dark : highlightColor.light,
        fontSize: 5,
        fontWeight: "600",
        fontFamily: FONT_FAMILY,
      },
      {
        fill: isDark ? highlightColor.dark : highlightColor.light,
        fontSize: 5,
        fontWeight: "600",
        fontFamily: FONT_FAMILY,
      },
    ]}
    flyoutPadding={{ top: 3, bottom: 3, left: 5, right: 5 }}
    cornerRadius={2}
    pointerLength={2}
  />
);

/** Cyan highlight colors for current player and other players tooltips */
const CYAN_HIGHLIGHT: HighlightColor = { dark: "#22d3ee", light: "#0891b2" };

/** Orange highlight colors for comparison player tooltips */
const ORANGE_HIGHLIGHT: HighlightColor = { dark: "#f97316", light: "#ea580c" };

/**
 * Get axis style based on theme
 * @param isDark - Whether dark mode is active
 * @returns Victory axis style object
 */
const getAxisStyle = (isDark: boolean) => ({
  axis: { stroke: isDark ? "#3f3f46" : "#d4d4d8", strokeWidth: 1 },
  tickLabels: {
    fill: isDark ? "#71717a" : "#a1a1aa",
    fontSize: 8,
    fontFamily: FONT_FAMILY,
  },
  grid: { stroke: isDark ? "#27272a" : "#e4e4e7", strokeDasharray: "4,4" },
});

/*
 * design-system: dynamic-style waiver — Victory scatter `style` is a chart-library
 * SVG style object with data-driven series fill (no NativeWind className surface);
 * mirrors getAxisStyle() so no inline object literal renders on the JSX element.
 */
const scatterDataStyle = (fill: string, opacity?: number) => ({
  data: opacity == null ? { fill } : { fill, opacity },
});

/**
 * DotPlotView renders the 2D scatter plot with shell-hosted axis selectors
 * @param props - Component props
 * @param props.players - Player data points
 * @param props.xMetric - X-axis metric
 * @param props.yMetric - Y-axis metric
 * @param props.availableMetrics - Available metrics for selection
 * @param props.isDark - Whether dark mode is active
 * @param props.size - Chart size
 * @param props.renderYMenuTrigger - Render function for Y-axis menu trigger
 * @param props.renderXMenuTrigger - Render function for X-axis menu trigger
 * @param props.createYSelectHandler - Factory for Y-axis metric select handlers
 * @param props.createXSelectHandler - Factory for X-axis metric select handlers
 * @param props.xAxisDropdown - Shell wiring for the X-axis dropdown (SE-5568)
 * @param props.yAxisDropdown - Shell wiring for the Y-axis dropdown (SE-5568)
 * @param props.tooltipLabelFormatter - Tooltip label formatter function
 * @returns Box containing the scatter plot with shell-hosted selectors
 */
const DotPlotView = ({
  players,
  xMetric,
  yMetric,
  availableMetrics,
  isDark,
  size,
  renderYMenuTrigger,
  renderXMenuTrigger,
  createYSelectHandler,
  createXSelectHandler,
  xAxisDropdown,
  yAxisDropdown,
  tooltipLabelFormatter,
}: DotPlotViewProps) => {
  // SE-6293: axes follow the data instead of a fixed 0–100 grid, so a rating
  // above 100 stays inside the drawable area rather than vanishing off-chart.
  const axisScales = useMemo(
    () => resolveDotPlotAxisScales(players),
    [players]
  );

  return (
    <Stack
      {...{
        testID: TEST_ID_PLAYER_DETAIL.DOT_PLOT,
        "data-testid": TEST_ID_PLAYER_DETAIL.DOT_PLOT,
        accessibilityLabel: "Player performance distribution",
        "aria-label": "Player performance distribution",
        // role="img" was removed because this wrapper also contains
        // interactive Menu selectors; role=img marks a leaf and hides
        // descendant semantics from assistive tech. The aria-label still
        // names the chart region for screen readers.
      }}
      className="relative w-full"
    >
      {/* Y-axis selector */}
      <Box className="self-start">
        {renderMetricShellDropdown({
          wiring: yAxisDropdown,
          trigger: renderYMenuTrigger({
            isOpen: yAxisDropdown.isOpen,
            onPress: yAxisDropdown.onToggle,
            triggerRef: yAxisDropdown.triggerRef,
          }),
          options: availableMetrics.map(metric =>
            renderMetricShellOption({
              label: metric.label,
              value: metric.value,
              isSelected: metric.label === yMetric.label,
              onPress: createYSelectHandler(metric),
              isDark,
            })
          ),
        })}
      </Box>

      {/* Chart */}
      <Box className="items-center justify-center">
        <VictoryChart
          width={size}
          height={size}
          domain={{ x: [0, axisScales.x.max], y: [0, axisScales.y.max] }}
          padding={{ top: 8, bottom: 28, left: 32, right: 8 }}
        >
          {/* X Axis */}
          <VictoryAxis
            tickValues={axisScales.x.tickValues}
            // eslint-disable-next-line no-restricted-syntax -- Victory chart-lib style API (SVG axis/tick/grid styling), not an RN/atom style
            style={getAxisStyle(isDark)}
          />
          {/* Y Axis */}
          <VictoryAxis
            dependentAxis
            tickValues={axisScales.y.tickValues}
            // eslint-disable-next-line no-restricted-syntax -- Victory chart-lib style API (SVG axis/tick/grid styling), not an RN/atom style
            style={getAxisStyle(isDark)}
          />

          {/* Other players - filtered to exclude null coordinates */}
          <VictoryScatter
            data={players.filter(
              p =>
                !p.isCurrentPlayer &&
                !p.isComparisonPlayer &&
                p.x != null &&
                p.y != null
            )}
            x="x"
            y="y"
            size={2}
            // eslint-disable-next-line no-restricted-syntax -- Victory chart-lib style API (data-driven SVG series fill), not an RN/atom style
            style={scatterDataStyle(OTHER_PLAYER_COLOR, 0.8)}
            labels={tooltipLabelFormatter}
            labelComponent={getTooltipComponent(isDark, CYAN_HIGHLIGHT)}
          />

          {/* Comparison player - orange, hidden when coordinates are null */}
          <VictoryScatter
            data={players.filter(
              p => p.isComparisonPlayer && p.x != null && p.y != null
            )}
            x="x"
            y="y"
            size={4}
            // eslint-disable-next-line no-restricted-syntax -- Victory chart-lib style API (data-driven SVG series fill), not an RN/atom style
            style={scatterDataStyle(COMPARISON_PLAYER_COLOR)}
            labels={tooltipLabelFormatter}
            labelComponent={getTooltipComponent(isDark, ORANGE_HIGHLIGHT)}
          />

          {/* Current player - slightly larger, hidden when coordinates are null */}
          <VictoryScatter
            data={players.filter(
              p => p.isCurrentPlayer && p.x != null && p.y != null
            )}
            x="x"
            y="y"
            size={4}
            // eslint-disable-next-line no-restricted-syntax -- Victory chart-lib style API (data-driven SVG series fill), not an RN/atom style
            style={scatterDataStyle(PLAYER_COLOR)}
            labels={tooltipLabelFormatter}
            labelComponent={getTooltipComponent(isDark, CYAN_HIGHLIGHT)}
          />
        </VictoryChart>
      </Box>

      {/* X-axis selector - below chart */}
      <Box className="self-center">
        {renderMetricShellDropdown({
          wiring: xAxisDropdown,
          trigger: renderXMenuTrigger({
            isOpen: xAxisDropdown.isOpen,
            onPress: xAxisDropdown.onToggle,
            triggerRef: xAxisDropdown.triggerRef,
          }),
          options: availableMetrics.map(metric =>
            renderMetricShellOption({
              label: metric.label,
              value: metric.value,
              isSelected: metric.label === xMetric.label,
              onPress: createXSelectHandler(metric),
              isDark,
            })
          ),
        })}
      </Box>
    </Stack>
  );
};

DotPlotView.displayName = "DotPlotView";

export default React.memo(DotPlotView);
